import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SectionVariable } from '../types';
import { useSettings } from '../Settings';
import { getAssetUrl, resolveAssetUrl } from '../api/assets';
import { PortabilityModal } from './PortabilityModal';
import { RechargeModal } from './RechargeModal';
import { openPreferredAppLink, openWhatsAppChat, openSalesOrWhatsApp } from '../utils/links';
import { useTranslation, LanguageSwitcher, interpolate } from '../i18n';

interface HeaderProps {
  variables?: SectionVariable;
  name?: string;
}

function SmartphoneIconHeader({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M7.452 0H7.548C9.12343 0 10.3714 0 11.3477 0.128093C12.3523 0.260372 13.1657 0.538326 13.8077 1.16456C14.4489 1.79163 14.7334 2.58614 14.8689 3.56735C15 4.52177 15 5.73991 15 7.2787V10.7213C15 12.2601 15 13.4791 14.8689 14.4327C14.7334 15.4139 14.4489 16.2084 13.8077 16.8354C13.1657 17.4617 12.3523 17.7396 11.3477 17.8719C10.3706 18 9.12343 18 7.548 18H7.452C5.87657 18 4.62857 18 3.65229 17.8719C2.64771 17.7396 1.83429 17.4617 1.19229 16.8354C0.551143 16.2084 0.266572 15.4139 0.131143 14.4327C1.02179e-07 13.4782 0 12.2601 0 10.7213V7.2787C0 5.73991 1.02179e-07 4.52093 0.131143 3.56735C0.266572 2.58614 0.551143 1.79163 1.19229 1.16456C1.83429 0.538326 2.64771 0.260372 3.65229 0.128093C4.62943 0 5.87657 0 7.452 0ZM3.82286 1.37302C2.96057 1.48605 2.46343 1.6987 2.1 2.05284C1.73829 2.40698 1.52057 2.89256 1.40486 3.73479C1.28657 4.59544 1.28486 5.72902 1.28486 7.32558V10.6744C1.28486 12.271 1.28657 13.4054 1.40486 14.266C1.52057 15.1074 1.73829 15.593 2.10086 15.9472C2.46343 16.3013 2.96057 16.514 3.82286 16.627C4.704 16.7425 5.86457 16.7442 7.49914 16.7442C9.13371 16.7442 10.2951 16.7425 11.1763 16.627C12.0377 16.514 12.5349 16.3013 12.8974 15.9472C13.26 15.593 13.4777 15.1074 13.5934 14.2652C13.7117 13.4054 13.7134 12.271 13.7134 10.6744V7.32558C13.7134 5.72902 13.7117 4.59544 13.5934 3.73395C13.4777 2.89256 13.26 2.40698 12.8974 2.05284C12.5349 1.6987 12.0377 1.48605 11.1754 1.37302C10.2943 1.25749 9.13371 1.25581 7.49914 1.25581C5.86457 1.25581 4.70486 1.25749 3.82286 1.37302ZM4.28571 3.13953C4.28571 2.973 4.35344 2.81329 4.474 2.69554C4.59456 2.57778 4.75808 2.51163 4.92857 2.51163H10.0714C10.2419 2.51163 10.4054 2.57778 10.526 2.69554C10.6466 2.81329 10.7143 2.973 10.7143 3.13953C10.7143 3.30607 10.6466 3.46578 10.526 3.58353C10.4054 3.70129 10.2419 3.76744 10.0714 3.76744H4.92857C4.75808 3.76744 4.59456 3.70129 4.474 3.58353C4.35344 3.46578 4.28571 3.30607 4.28571 3.13953ZM7.5 12.1395C7.21584 12.1395 6.94332 12.2498 6.74239 12.446C6.54145 12.6423 6.42857 12.9085 6.42857 13.186C6.42857 13.4636 6.54145 13.7298 6.74239 13.926C6.94332 14.1223 7.21584 14.2326 7.5 14.2326C7.78416 14.2326 8.05668 14.1223 8.25761 13.926C8.45855 13.7298 8.57143 13.4636 8.57143 13.186C8.57143 12.9085 8.45855 12.6423 8.25761 12.446C8.05668 12.2498 7.78416 12.1395 7.5 12.1395Z" fill="#4A4A4A"/>
    </svg>
  );
}

function ArrowCircleIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M9.16667 18.3333C14.2294 18.3333 18.3333 14.2294 18.3333 9.16667C18.3333 4.10392 14.2294 0 9.16667 0C4.10392 0 0 4.10392 0 9.16667C0 14.2294 4.10392 18.3333 9.16667 18.3333ZM9.5975 5.93083C9.72641 5.80209 9.90115 5.72977 10.0833 5.72977C10.2655 5.72977 10.4403 5.80209 10.5692 5.93083L13.3192 8.68083C13.4479 8.80974 13.5202 8.98448 13.5202 9.16667C13.5202 9.34886 13.4479 9.52359 13.3192 9.6525L10.5692 12.4025C10.5062 12.47 10.4303 12.5242 10.346 12.5618C10.2617 12.5994 10.1706 12.6196 10.0783 12.6212C9.986 12.6228 9.89431 12.6059 9.8087 12.5713C9.7231 12.5367 9.64533 12.4852 9.58005 12.42C9.51477 12.3547 9.4633 12.2769 9.42872 12.1913C9.39415 12.1057 9.37716 12.014 9.37879 11.9217C9.38042 11.8294 9.40063 11.7383 9.4382 11.654C9.47578 11.5697 9.52996 11.4938 9.5975 11.4308L11.1742 9.85417H5.5C5.31766 9.85417 5.1428 9.78174 5.01386 9.6528C4.88493 9.52387 4.8125 9.349 4.8125 9.16667C4.8125 8.98433 4.88493 8.80946 5.01386 8.68053C5.1428 8.5516 5.31766 8.47917 5.5 8.47917H11.1742L9.5975 6.9025C9.46876 6.77359 9.39644 6.59886 9.39644 6.41667C9.39644 6.23448 9.46876 6.05974 9.5975 5.93083Z" fill="var(--color-primary, #6B00D6)"/>
    </svg>
  );
}

/* Hamburger icon: 3 horizontal lines with rounded caps, using CSS variable color */
function HamburgerIcon() {
  return (
    <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
      <rect y="0" width="20" height="2" rx="1" fill="var(--color-primary, #6B00D6)" />
      <rect y="7" width="20" height="2" rx="1" fill="var(--color-primary, #6B00D6)" />
      <rect y="14" width="20" height="2" rx="1" fill="var(--color-primary, #6B00D6)" />
    </svg>
  );
}

export function Header({ variables }: HeaderProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeNav, setActiveNav] = useState('/');
  const [portabilityOpen, setPortabilityOpen] = useState(false);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { app, settings } = useSettings();
  const { t } = useTranslation();
  const appLinks = settings.appLinks;
  const middlewareUrl = settings.middlewareUrl || '';

  const navLinks = [
    { key: 'home', label: t.header.home, href: '/' },
    { key: 'plans', label: t.header.plans, href: '#planos' },
    { key: 'portability', label: t.header.portability, href: '#portabilidade' },
    { key: 'advantages', label: t.header.advantages, href: '#vantagens' },
  ];

  function handleBuy() {
    const message = interpolate(t.whatsapp.salesMessage, {
      brand: settings.companyName || t.whatsapp.salesFallbackBrand,
    });
    openSalesOrWhatsApp(settings.salesUrl, settings.socials?.whatsapp, message);
  }

  const logoSrc = variables?.image?.[0]
    ? resolveAssetUrl(variables.image[0], app)
    : getAssetUrl('images/logos/logo.svg', app);

  const texts = variables?.text || [];
  const hideAppButton = settings.hideApp === true || texts[0] === 'true';

  const hasAppLinks = !!(appLinks?.playStore || appLinks?.appStore);

  // Pending action to run AFTER drawer close animation finishes
  const pendingAction = useRef<(() => void) | null>(null);

  function closeDrawerThen(action: () => void) {
    pendingAction.current = action;
    setDrawerOpen(false);
  }

  function handleDrawerExited() {
    if (pendingAction.current) {
      pendingAction.current();
      pendingAction.current = null;
    }
  }

  // Track active section based on scroll
  useEffect(() => {
    function handleScroll() {
      const sections = ['vantagens', 'portabilidade', 'planos'];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom > 100) {
            setActiveNav(`#${id}`);
            return;
          }
        }
      }
      setActiveNav('/');
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  function handleAppDownload() {
    openPreferredAppLink(appLinks);
  }

  const mobileNavItems = [
    {
      label: t.header.home,
      action: () => {
        closeDrawerThen(() => {
          window.location.href = '/';
          window.location.reload();
        });
      },
    },
    {
      label: t.header.plans,
      action: () => {
        closeDrawerThen(() => {
          const el = document.getElementById('planos');
          if (el) {
            const y = el.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        });
      },
    },
    {
      label: t.header.portability,
      action: () => {
        closeDrawerThen(() => setPortabilityOpen(true));
      },
    },
    {
      label: t.header.recharge,
      action: () => {
        closeDrawerThen(() => setRechargeOpen(true));
      },
    },
    {
      label: t.header.advantages,
      action: () => {
        closeDrawerThen(() => {
          document.getElementById('vantagens')?.scrollIntoView({ behavior: 'smooth' });
        });
      },
    },
    {
      label: t.header.customerService,
      action: () => {
        closeDrawerThen(() => {
          openWhatsAppChat(settings.socials?.whatsapp);
        });
      },
    },
    {
      label: t.header.helpCenter,
      action: () => {
        closeDrawerThen(() => {
          document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
        });
      },
    },
  ];

  return (
    <>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{
          bgcolor: '#fff',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            maxWidth: 1400,
            width: '100%',
            mx: 'auto',
            px: { xs: 2, md: 3 },
            minHeight: { xs: 56, md: 64 },
          }}
        >
          {/* Left: Desktop Nav */}
          {!isMobile && (
            <Box display="flex" alignItems="center" gap={0.5} flex={1}>
              {navLinks.map((link) => {
                const isActive = activeNav === link.href;
                return (
                  <Box
                    key={link.key}
                    component="a"
                    href={link.href}
                    onClick={(e) => {
                      if (link.key === 'portability') {
                        e.preventDefault();
                        setPortabilityOpen(true);
                      } else {
                        setActiveNav(link.href);
                      }
                    }}
                    sx={{
                      px: 2,
                      py: 0.75,
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? '#FFFFFF' : '#6C6C6C',
                      bgcolor: isActive ? 'var(--color-primary, #6B00D6)' : 'transparent',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        bgcolor: isActive ? 'var(--color-primary, #6B00D6)' : '#0000000A',
                      },
                    }}
                  >
                    {link.label}
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Center: Logo */}
          <Box
            component="a"
            href="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              flex: { xs: 1, md: '0 0 auto' },
              justifyContent: { xs: 'flex-start', md: 'center' },
              px: { md: 4 },
            }}
          >
            <Box
              component="img"
              src={logoSrc}
              alt="Logo"
              sx={{ height: { xs: 24, md: 28 } }}
            />
          </Box>

          {/* Right: Desktop Actions */}
          {!isMobile && (
            <Box display="flex" alignItems="center" gap={1.5} flex={1} justifyContent="flex-end">
              <LanguageSwitcher />
              {!hideAppButton && (
                <Button
                  onClick={handleAppDownload}
                  startIcon={<SmartphoneIconHeader size={16} />}
                  variant="text"
                  sx={{
                    color: '#333',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.85rem',
                  }}
                >
                  {t.common.downloadApp}
                </Button>
              )}
              <Button
                onClick={handleBuy}
                variant="text"
                endIcon={<ArrowCircleIcon size={16} />}
                sx={{
                  color: 'var(--color-primary, #6B00D6)',
                  borderRadius: '24px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  fontSize: '0.85rem',
                  '&:hover': {
                    bgcolor: '#6B00D60F',
                  },
                }}
              >
                {t.common.startNow}
              </Button>
            </Box>
          )}

          {/* Mobile: language switcher + hamburger */}
          {isMobile && (
            <Box display="flex" alignItems="center" gap={1}>
              <LanguageSwitcher compact />
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{
                  bgcolor: '#6B00D614',
                  borderRadius: '12px',
                  width: 40,
                  height: 40,
                  '&:hover': { bgcolor: '#6B00D624' },
                }}
              >
                <HamburgerIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer – Fullscreen Purple */}
      <Drawer
        anchor="top"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        slotProps={{ transition: { onExited: handleDrawerExited } }}
        PaperProps={{
          sx: {
            width: '100%',
            height: '100vh',
            bgcolor: 'var(--color-primary, #6B00D6)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          },
        }}
      >
        {/* Top: Logo + Close */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          px={3}
          pt={3}
          pb={2}
        >
          <Box flex={1} />
          <Box
            component="img"
            src={getAssetUrl('images/logos/logo-white.svg', app)}
            alt="Logo"
            sx={{ height: 28 }}
          />
          <Box flex={1} display="flex" justifyContent="flex-end">
            <IconButton
              onClick={() => setDrawerOpen(false)}
              sx={{
                color: '#fff',
                bgcolor: '#FFFFFF26',
                width: 36,
                height: 36,
                '&:hover': { bgcolor: '#FFFFFF40' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Nav Links */}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1}
          mt={6}
          flex={1}
        >
          {mobileNavItems.map((item) => (
            <Box
              key={item.label}
              component="button"
              onClick={item.action}
              sx={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#fff',
                fontFamily: "'Figtree', sans-serif",
                fontSize: '1rem',
                fontWeight: 500,
                py: 1.5,
                '&:hover': { opacity: 0.8 },
              }}
            >
              {item.label}
            </Box>
          ))}
        </Box>

        {/* Bottom CTA + Store Badges */}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={2}
          pb={4}
          px={3}
          width="100%"
        >
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              closeDrawerThen(() => handleBuy());
            }}
            sx={{
              borderRadius: '2.8125rem',
              background: 'var(--color-accent, #00E5A0)',
              color: '#fff',
              textTransform: 'none',
              fontFamily: "'Figtree', sans-serif",
              fontWeight: 600,
              fontSize: '1rem',
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              boxShadow: 'none',
              maxWidth: 280,
              '&:hover': { background: 'var(--color-accent, #00E5A0)', opacity: 0.9, boxShadow: 'none' },
            }}
          >
            {t.common.getMyChip}
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: '#FFFFFF40',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Box>
          </Button>

          {!hideAppButton && hasAppLinks && (
            <>
              <Box
                component="a"
                onClick={() => { handleAppDownload(); setDrawerOpen(false); }}
                sx={{
                  color: '#fff',
                  fontFamily: "'Figtree', sans-serif",
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  opacity: 0.9,
                }}
              >
                {t.common.downloadApp}
              </Box>

              <Box display="flex" gap={1.5}>
                {appLinks?.playStore && (
                  <Box
                    component="a"
                    href={appLinks.playStore}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Box
                      component="img"
                      src={getAssetUrl('images/app-section/google-play-badge.png', app)}
                      alt="Google Play"
                      sx={{ height: 36, objectFit: 'contain' }}
                    />
                  </Box>
                )}
                {appLinks?.appStore && (
                  <Box
                    component="a"
                    href={appLinks.appStore}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Box
                      component="img"
                      src={getAssetUrl('images/app-section/app-store-badge.png', app)}
                      alt="App Store"
                      sx={{ height: 36, objectFit: 'contain' }}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Drawer>

      {/* Modals (reused from desktop) */}
      <PortabilityModal
        open={portabilityOpen}
        onClose={() => setPortabilityOpen(false)}
        middlewareUrl={middlewareUrl}
      />
      <RechargeModal
        open={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        middlewareUrl={middlewareUrl}
      />
    </>
  );
}
